/**
 * Input Validation and Sanitization Utilities
 * Provides secure methods for validating and sanitizing user input
 */

import { sanitizeHtml, escapeHtml } from './htmlSanitizer';

/**
 * Email validation regex
 */
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Password validation regex (at least 8 chars, 1 upper, 1 lower, 1 number)
 */
// eslint-disable-next-line no-unused-vars
const STRONG_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

/**
 * SQL injection patterns to detect
 */
const SQL_INJECTION_PATTERNS = [
  /(\s*(union|select|insert|update|delete|drop|create|alter|exec|execute)\s+)/i,
  /((\s*;\s*)|(\s*--)|(\s*\/\*))/i,
  /(\s*(or|and)\s+\d+\s*=\s*\d+)/i
];

/**
 * XSS patterns to detect
 */
const XSS_PATTERNS = [
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi,
  /<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi
];

/**
 * Validates an email address
 * @param {string} email - The email to validate
 * @returns {Object} - {isValid: boolean, error?: string}
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return { isValid: false, error: 'Email is required' };
  }
  
  if (email.length > 254) {
    return { isValid: false, error: 'Email is too long' };
  }
  
  if (!EMAIL_REGEX.test(email)) {
    return { isValid: false, error: 'Invalid email format' };
  }
  
  return { isValid: true };
};

/**
 * Validates a password
 * @param {string} password - The password to validate
 * @param {Object} options - Validation options
 * @returns {Object} - {isValid: boolean, errors: string[]}
 */
export const validatePassword = (password, options = {}) => {
  const {
    minLength = 8,
    requireUppercase = true,
    requireLowercase = true,
    requireNumbers = true,
    requireSymbols = true
  } = options;
  
  const errors = [];
  
  if (!password || typeof password !== 'string') {
    errors.push('Password is required');
    return { isValid: false, errors };
  }
  
  if (password.length < minLength) {
    errors.push(`Password must be at least ${minLength} characters long`);
  }
  
  if (requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (requireNumbers && !/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (requireSymbols && !/[@$!%*?&]/.test(password)) {
    errors.push('Password must contain at least one special character (@$!%*?&)');
  }
  
  // Check for common weak passwords
  const commonPasswords = [
    'password', '123456', '123456789', 'qwerty', 'abc123', 
    'password123', 'admin', 'letmein', 'welcome', 'monkey'
  ];
  
  if (commonPasswords.includes(password.toLowerCase())) {
    errors.push('Password is too common and easily guessed');
  }
  
  return { isValid: errors.length === 0, errors };
};

/**
 * Validates a name field
 * @param {string} name - The name to validate
 * @param {Object} options - Validation options
 * @returns {Object} - {isValid: boolean, sanitized: string, error?: string}
 */
export const validateName = (name, options = {}) => {
  const { required = true, maxLength = 100, minLength = 2 } = options;
  
  if (!name || typeof name !== 'string') {
    if (required) {
      return { isValid: false, error: 'Name is required' };
    }
    return { isValid: true, sanitized: '' };
  }
  
  // Remove extra whitespace and sanitize
  const sanitized = name.trim().replace(/\s+/g, ' ');
  
  if (sanitized.length < minLength) {
    return { isValid: false, error: `Name must be at least ${minLength} characters long` };
  }
  
  if (sanitized.length > maxLength) {
    return { isValid: false, error: `Name must be no more than ${maxLength} characters long` };
  }
  
  // Check for potentially malicious content
  if (containsXSS(sanitized) || containsSQLInjection(sanitized)) {
    return { isValid: false, error: 'Name contains invalid characters' };
  }
  
  // Only allow letters, spaces, hyphens, and apostrophes
  if (!/^[a-zA-Z\s\-']+$/.test(sanitized)) {
    return { isValid: false, error: 'Name can only contain letters, spaces, hyphens, and apostrophes' };
  }
  
  return { isValid: true, sanitized };
};

/**
 * Validates a text field
 * @param {string} text - The text to validate
 * @param {Object} options - Validation options
 * @returns {Object} - {isValid: boolean, sanitized: string, errors: string[]}
 */
export const validateText = (text, options = {}) => {
  const {
    required = false,
    maxLength = 1000,
    minLength = 0,
    allowHtml = false,
    preventXSS = true,
    preventSQLInjection = true
  } = options;
  
  const errors = [];
  
  if (!text || typeof text !== 'string') {
    if (required) {
      errors.push('This field is required');
    }
    return { isValid: !required, sanitized: '', errors };
  }
  
  let sanitized = text.trim();
  
  if (sanitized.length < minLength) {
    errors.push(`Text must be at least ${minLength} characters long`);
  }
  
  if (sanitized.length > maxLength) {
    errors.push(`Text must be no more than ${maxLength} characters long`);
  }
  
  if (preventXSS && containsXSS(sanitized)) {
    errors.push('Text contains potentially dangerous content');
  }
  
  if (preventSQLInjection && containsSQLInjection(sanitized)) {
    errors.push('Text contains invalid characters');
  }
  
  // Sanitize based on options
  if (!allowHtml) {
    sanitized = escapeHtml(sanitized);
  } else {
    sanitized = sanitizeHtml(sanitized);
  }
  
  return { isValid: errors.length === 0, sanitized, errors };
};

/**
 * Validates a file upload
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @returns {Object} - {isValid: boolean, errors: string[]}
 */
export const validateFile = (file, options = {}) => {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
    allowedMimeTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png'
    ]
  } = options;
  
  const errors = [];
  
  if (!file) {
    errors.push('File is required');
    return { isValid: false, errors };
  }
  
  // Check file size
  if (file.size > maxSize) {
    errors.push(`File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`);
  }
  
  // Check file extension
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  if (!fileExtension || !allowedTypes.includes(fileExtension)) {
    errors.push(`File type not allowed. Allowed types: ${allowedTypes.join(', ')}`);
  }
  
  // Check MIME type
  if (!allowedMimeTypes.includes(file.type)) {
    errors.push('Invalid file type detected');
  }
  
  // Check for potentially dangerous filenames
  if (file.name.includes('..') || file.name.includes('/') || file.name.includes('\\')) {
    errors.push('Invalid filename');
  }
  
  return { isValid: errors.length === 0, errors };
};

/**
 * Validates a phone number
 * @param {string} phone - The phone number to validate
 * @returns {Object} - {isValid: boolean, sanitized?: string, error?: string}
 */
export const validatePhoneNumber = (phone) => {
  if (!phone || typeof phone !== 'string') {
    return { isValid: false, error: 'Phone number is required' };
  }
  
  // Remove all non-digit characters except +
  const sanitized = phone.replace(/[^\d+]/g, '');
  
  // Basic phone number validation (international format)
  const phoneRegex = /^(\+\d{1,3}[- ]?)?\d{10,14}$/;
  
  if (!phoneRegex.test(sanitized)) {
    return { isValid: false, error: 'Invalid phone number format' };
  }
  
  return { isValid: true, sanitized };
};

/**
 * Validates a South African postal code
 * @param {string} postalCode - The postal code to validate
 * @returns {Object} - {isValid: boolean, sanitized?: string, error?: string}
 */
export const validatePostalCode = (postalCode) => {
  if (!postalCode || typeof postalCode !== 'string') {
    return { isValid: false, error: 'Postal code is required' };
  }

  // Remove spaces and trim
  const sanitized = postalCode.trim().replace(/\s/g, '');

  // South African postal codes are 4 digits
  if (!/^\d{4}$/.test(sanitized)) {
    return { isValid: false, error: 'Postal code must be exactly 4 digits' };
  }

  return { isValid: true, sanitized };
};

/**
 * Checks if text contains XSS patterns
 * @param {string} text - The text to check
 * @returns {boolean} - True if XSS patterns are found
 */
const containsXSS = (text) => {
  return XSS_PATTERNS.some(pattern => pattern.test(text));
};

/**
 * Checks if text contains SQL injection patterns
 * @param {string} text - The text to check
 * @returns {boolean} - True if SQL injection patterns are found
 */
const containsSQLInjection = (text) => {
  return SQL_INJECTION_PATTERNS.some(pattern => pattern.test(text));
};

/**
 * Sanitizes a complete form object
 * @param {Object} formData - The form data to sanitize
 * @param {Object} schema - The validation schema
 * @returns {Object} - {isValid: boolean, sanitized: Object, errors: Object}
 */
export const validateForm = (formData, schema) => {
  const sanitized = {};
  const errors = {};
  let isValid = true;
  
  for (const [field, rules] of Object.entries(schema)) {
    const value = formData[field];
    let fieldResult;
    
    switch (rules.type) {
      case 'email':
        fieldResult = validateEmail(value);
        break;
      case 'password':
        fieldResult = validatePassword(value, rules.options);
        break;
      case 'name':
        fieldResult = validateName(value, rules.options);
        break;
      case 'text':
        fieldResult = validateText(value, rules.options);
        break;
      case 'phone':
        fieldResult = validatePhoneNumber(value);
        break;
      default:
        fieldResult = { isValid: true, sanitized: value };
    }
    
    if (!fieldResult.isValid) {
      isValid = false;
      errors[field] = fieldResult.error || fieldResult.errors;
    }
    
    sanitized[field] = fieldResult.sanitized ?? value;
  }
  
  return { isValid, sanitized, errors };
};

/**
 * Rate limiting helper
 */
export class RateLimiter {
  constructor(maxAttempts = 5, windowMs = 60000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
    this.attempts = new Map();
  }
  
  isAllowed(identifier) {
    const now = Date.now();
    const userAttempts = this.attempts.get(identifier) || { count: 0, resetTime: now + this.windowMs };
    
    if (now > userAttempts.resetTime) {
      userAttempts.count = 0;
      userAttempts.resetTime = now + this.windowMs;
    }
    
    if (userAttempts.count >= this.maxAttempts) {
      return false;
    }
    
    userAttempts.count++;
    this.attempts.set(identifier, userAttempts);
    return true;
  }
  
  getRemainingTime(identifier) {
    const userAttempts = this.attempts.get(identifier);
    if (!userAttempts) return 0;
    
    return Math.max(0, userAttempts.resetTime - Date.now());
  }
}